import type { Router } from 'vue-router'
import { usePermissionStore } from '@/stores/permission'
import type { PermissionedRouteCandidate } from '@/config/businessRoutePermissions'
import type { PermissionRequirement } from '@/types/permission'
import { clearAuthClientState, hasMustChangePasswordFlag } from '@/utils/authState'
import { getDefaultRoute, getFirstAccessibleRoute } from '@/utils/defaultRoute'
import {
  getCurrentTenantCode,
  isSubdomainMode,
  normalizeTenantCode,
  replaceTenantCodeInPath,
  routeParamToString,
  stripTenantPath,
  toTenantPath,
} from '@/utils/tenantRoute'
import { message } from '@/utils/toast'
import { showApiError } from '@/utils/apiError'

type TenantLike = {
  id?: number | string
  name?: string
  code?: string
}

export function setupRouterGuards(router: Router) {
  router.beforeEach(async (to, _from, next) => {
    const publicPages = ['/login', '/register', '/forgot-password', '/sso/callback', '/403']
    const publicRouteNames = ['login', 'register', 'forgot-password', 'sso-callback', 'Forbidden', 'NotFound']
    const routeName = typeof to.name === 'string' ? to.name : ''
    const isIncidentH5Entry = /^\/t\/[^/]+\/incident-assistant\/?$/.test(to.path)
    const isPublicRoute = isIncidentH5Entry
      || Boolean(to.meta?.public)
      || (to.matched || []).some((record: any) => Boolean(record.meta?.public))
    const authRequired = !isPublicRoute && !publicPages.includes(to.path) && !publicRouteNames.includes(routeName)
    const user = localStorage.getItem('user')
    const token = localStorage.getItem('token')
    const isAuthenticated = Boolean(user && token)

    if (authRequired && !isAuthenticated) {
      clearAuthClientState()
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }

    if (!authRequired) {
      next()
      return
    }

    if (isAuthenticated) {
      try {
        const parsedUser = JSON.parse(user || '{}')
        if (hasMustChangePasswordFlag(parsedUser) && to.path !== '/force-change-password') {
          next('/force-change-password')
          return
        }
      } catch {
        // ignore malformed local user state; normal auth checks handle it
      }

      const permStore = usePermissionStore()
      permStore.loadPermissions()
      const { ensureAuthSessionReady } = await import('@/services/authSession')
      const sessionResult = await ensureAuthSessionReady({ reason: 'router.beforeEach' })

      if (!sessionResult.ok) {
        clearAuthClientState()
        next({ path: '/login', query: { redirect: to.fullPath } })
        return
      }

      const tenantMismatchRedirect = await resolveTenantRouteMismatch(to)
      if (tenantMismatchRedirect) {
        next(tenantMismatchRedirect)
        return
      }

      if (to.path === '/') {
        next(getDefaultRoute(permStore.permissions))
        return
      }

      const defaultRoute = getMatchedDefaultRoute(to, permStore.permissions)
      if (defaultRoute && defaultRoute !== to.fullPath) {
        next(defaultRoute)
        return
      }

      // 权限路由检查（如果路由配置了 meta.permission）
      const requiredPermission = to.meta?.permission as PermissionRequirement | undefined
      if (requiredPermission && !permStore.canAccess(requiredPermission)) {
        const refreshedSession = await ensureAuthSessionReady({
          force: true,
          reason: 'router.beforeEach:permission-denied-check'
        })
        if (refreshedSession.ok && permStore.canAccess(requiredPermission)) {
          next()
          return
        }
        if (
          !refreshedSession.ok
          && refreshedSession.reason === 'AUTH_REQUEST_FAILED'
          && refreshedSession.retryable === false
        ) {
          clearAuthClientState()
          next({ path: '/login', query: { redirect: to.fullPath } })
          return
        }
        next('/403')
        return
      }
    }

    next()
  })
}

function getMatchedDefaultRoute(to: any, permissions: string[]): string | undefined {
  const matchedWithDefault = [...(to.matched || [])].reverse().find((record: any) => {
    return isDefaultRouteBasePath(to.path, record.meta?.defaultRouteBasePath, to.params)
      && Array.isArray(record.meta?.defaultRouteCandidates)
  })

  if (!matchedWithDefault) return undefined

  const defaultRoute = getFirstAccessibleRoute(
    matchedWithDefault.meta.defaultRouteCandidates as PermissionedRouteCandidate[],
    permissions
  )

  return defaultRoute ? toTenantPath(defaultRoute, routeParamToString(to.params?.tenantCode)) : undefined
}

async function resolveTenantRouteMismatch(to: any): Promise<string | undefined> {
  // 子域名模式下无需检查租户路由匹配
  if (isSubdomainMode()) return undefined

  const routeTenantCode = normalizeTenantCode(routeParamToString(to.params?.tenantCode))
  if (!routeTenantCode) return undefined

  const currentTenantCode = getCurrentTenantCode()
  if (!currentTenantCode || currentTenantCode === routeTenantCode) return undefined

  const targetTenant = readStoredTenants().find(tenant => normalizeTenantCode(tenant.code) === routeTenantCode)
  if (!targetTenant?.id) {
    message.warning('当前链接不属于当前组织', '已为你切回当前组织下的同一页面')
    return replaceTenantCodeInPath(to.fullPath, currentTenantCode)
  }

  const switched = await switchTenantForRoute(targetTenant, routeTenantCode)
  if (switched) return undefined

  const currentCodeAfterFailure = getCurrentTenantCode() || currentTenantCode
  return currentCodeAfterFailure
    ? replaceTenantCodeInPath(to.fullPath, currentCodeAfterFailure)
    : stripTenantPath(to.fullPath)
}

async function switchTenantForRoute(tenant: TenantLike, routeTenantCode: string): Promise<boolean> {
  try {
    const [{ switchTenant }, { useAuthSession }, { cancelAllRequests }, { useReferenceDataStore }] = await Promise.all([
      import('@/api/system/auth'),
      import('@/composables/useAuthSession'),
      import('@/api/request'),
      import('@/stores/referenceData'),
    ])
    const session = await switchTenant({ tenantId: tenant.id! })
    useAuthSession().saveAuthSession(session as any)
    useReferenceDataStore().clearReferenceData()
    cancelAllRequests()

    if (getCurrentTenantCode() !== routeTenantCode) {
      message.warning('租户切换未完成', '已为你返回当前组织页面')
      return false
    }

    message.success('已切换组织', tenant.name ? `当前组织：${tenant.name}` : undefined)
    return true
  } catch (error) {
    showApiError(error, '切换组织失败')
    return false
  }
}

function readStoredTenants(): TenantLike[] {
  try {
    const raw = localStorage.getItem('tenants')
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function isDefaultRouteBasePath(path: string, basePath: unknown, params: Record<string, any> = {}): boolean {
  if (typeof basePath !== 'string') return false
  return path === basePath || path === fillRouteParams(basePath, params)
}

function fillRouteParams(path: string, params: Record<string, any>): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, key) => routeParamToString(params[key]))
}
